import { Box, Divider, Flex, Text } from '@chakra-ui/layout';
import { Menu, MenuButton, MenuItem, MenuList } from '@chakra-ui/menu';
import {
  Icon,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Portal,
  Spinner,
  Tag,
  Tooltip,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useMutation } from '@tanstack/react-query';
import type { ColumnDef, SortingState } from '@tanstack/react-table';
import { IconArrowRotateLeftRight } from 'central-icons/IconArrowRotateLeftRight';
import { IconBookmark } from 'central-icons/IconBookmark';
import { IconChainLink2 } from 'central-icons/IconChainLink2';
import { IconDotGrid1x3Vertical } from 'central-icons/IconDotGrid1x3Vertical';
import { IconFileDownload } from 'central-icons/IconFileDownload';
import { IconPageEdit } from 'central-icons/IconPageEdit';
import { IconStop } from 'central-icons/IconStop';
import { formatDistanceToNow, intlFormat } from 'date-fns';
import {
  isValidElement,
  type ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';

import { isRoleRevelioAdmin, useGetLoggedInUser } from '@revelio/auth';
import { FeatureFlag, PagePaths, PageTitles, WithTooltip } from '@revelio/core';
import { TableChart } from '@revelio/replots';
import { Button, FormField, Input } from '@revelio/ui';

import { showAlertDialog } from '../../shared/components/alert-dialog';
import { useKibanaLogger } from '../../shared/hooks/kibana-logger/kibana-logger';
import {
  type StatusTone,
  ThemedStatusTag,
} from '../../shared/ui/table/themed-status-tag';
import { useUnleashFlag } from '../../shared/unleash-flags/use-unleash-flag';
import { useIsThemedAppearance } from '../../shared/user-preferences/hooks/useIsThemedAppearance';
import { useEffectiveUserId } from '../adminRewrite/impersonation/useEffectiveUserId';
import DashboardPage from '../DashboardPage';
import {
  cancelDataBuilderRun,
  fetchDataBuilderStatus,
  shareDataBuilderRun,
} from './api/data-builder-api';
import type {
  DataBuilderStatus as DataBuilderApiStatus,
  DataBuilderPayload,
  DataBuilderStatusGetResponse,
} from './api/data-builder-api.types';
import { withDataBuilderMutationPolicy } from './api/data-builder-request-policies';
import { hydrateStoreFromPayload } from './api/hydrate-store-from-payload';
import { useDatasetStorage } from './api/useDatasetStorage';
import { type UserOption, UserSelector } from './components/user-selector';
import { DataBuilderStatusDetailsModal } from './DataBuilderStatusDetailsModal';
import { dataBuilderStore } from './data-builder.store';
import styles from './data-builder-status.module.css';
import { DataBuilderPaths } from './steps/data-builder-steps';

export const defaultTagColors = { bgColor: 'gray.100', color: 'gray.600' };

const POLL_MS = 4 * 60 * 1000;

export const dataBuilderTagStatusColorMap: Partial<
  Record<DataBuilderApiStatus, { bgColor: string; color: string }>
> = {
  RUNNING: {
    bgColor: 'blue.100',
    color: 'blue.600',
  },
  FAILED: {
    bgColor: 'red.100',
    color: 'red.600',
  },
  FINISHED: {
    bgColor: 'green.100',
    color: 'green.600',
  },
  FINISHED_WITH_EMPTY: {
    bgColor: 'green.100',
    color: 'green.600',
  },
  CANCELLED: {
    bgColor: 'gray.100',
    color: 'gray.600',
  },
};

export const dataBuilderTagStatusTone: Partial<
  Record<DataBuilderApiStatus, StatusTone>
> = {
  RUNNING: 'inProgress',
  FAILED: 'failed',
  FINISHED: 'finished',
  FINISHED_WITH_EMPTY: 'finished',
  CANCELLED: 'neutral',
};

function isFinishedDataBuilderStatus(status: DataBuilderApiStatus): boolean {
  return status === 'FINISHED' || status === 'FINISHED_WITH_EMPTY';
}

export function isFinishedWithEmptyStatus(
  status: DataBuilderApiStatus
): boolean {
  return status === 'FINISHED_WITH_EMPTY';
}

export const FINISHED_WITH_EMPTY_TOOLTIP =
  'Finished but some results have no data. This is typically caused by adding filters that are too specific, or by choosing very small companies that have no public profiles, job postings, or sentiment reviews.';

export function getDataBuilderStatusLabel(
  status: DataBuilderApiStatus
): string {
  if (isFinishedWithEmptyStatus(status)) {
    return 'FINISHED *';
  }
  return status;
}

export function isDeliveryDownloadable(
  status: DataBuilderApiStatus,
  url: string | null
): boolean {
  if (!isFinishedDataBuilderStatus(status)) {
    return false;
  }
  if (url === null || url.trim().length === 0) {
    return false;
  }
  return true;
}

function hasDownloadDeliveryMethod(payload: DataBuilderPayload): boolean {
  return payload.deliverables.some(
    (deliverable) => deliverable.delivery.type === 'download'
  );
}

type SaveConfigModalProps = {
  isOpen: boolean;
  onClose: () => void;
  defaultName: string;
  payload: DataBuilderPayload;
};

const SaveConfigModal = ({
  isOpen,
  onClose,
  defaultName,
  payload,
}: SaveConfigModalProps) => {
  const toast = useToast();
  const { saveDataset } = useDatasetStorage();
  const [configName, setConfigName] = useState(defaultName);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setConfigName(defaultName);
    }
  }, [isOpen, defaultName]);

  const trimmedName = configName.trim();

  const handleSave = () => {
    if (trimmedName.length === 0) {
      return;
    }

    setSaving(true);
    void (async () => {
      try {
        await saveDataset({ name: trimmedName, payload });
        toast({
          title: 'Delivery config saved',
          status: 'success',
          duration: 4000,
          isClosable: true,
        });
        onClose();
      } catch (error: unknown) {
        const message =
          error instanceof Error ? error.message : 'Unknown error occurred';
        toast({
          title: 'Failed to save delivery config',
          description: message,
          status: 'error',
          duration: 6000,
          isClosable: true,
        });
      } finally {
        setSaving(false);
      }
    })();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader color="text.primary">Save Delivery Config</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormField
            label="Config name"
            errors={
              trimmedName.length === 0 ? ['A name is required'] : undefined
            }
          >
            <Input
              value={configName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setConfigName(e.target.value)
              }
              placeholder="Enter config name"
              data-testid="data_builder_v2_save_config_name_input"
              autoFocus
            />
          </FormField>
        </ModalBody>
        <ModalFooter gap="3">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleSave}
            disabled={saving || trimmedName.length === 0}
            data-testid="data_builder_v2_save_config_confirm"
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

type ShareLinkModalProps = {
  isOpen: boolean;
  onClose: () => void;
  runId: number;
};

type ShareLinkModalContentProps = {
  runId: number;
  onClose: () => void;
};

const ShareLinkModalContent = ({
  runId,
  onClose,
}: ShareLinkModalContentProps) => {
  const toast = useToast();
  const { kibanaLogger } = useKibanaLogger();
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const {
    mutateAsync: createShareLink,
    isPending: loading,
    error: shareMutationError,
    reset: resetShareMutation,
  } = useMutation(
    withDataBuilderMutationPolicy(
      {
        mutationKey: ['data-builder', 'share'],
        mutationFn: shareDataBuilderRun,
      },
      kibanaLogger
    )
  );

  const error = (() => {
    if (shareMutationError instanceof Error) {
      return shareMutationError.message;
    }
    if (shareMutationError !== null && shareMutationError !== undefined) {
      return 'Unknown error occurred';
    }
    return null;
  })();

  const generateLink = async () => {
    resetShareMutation();
    setCopied(false);
    setShareUrl(null);
    try {
      const { token } = await createShareLink({ id: runId });
      setShareUrl(
        `${window.location.origin}/${PagePaths.DATA_BUILDER_SHARE}/${token}`
      );
    } catch {
      // Error state is surfaced by the mutation.
    }
  };

  const handleCopy = async () => {
    if (shareUrl === null) {
      return;
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast({
        title: 'Share link copied',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch {
      toast({
        title: 'Failed to copy link',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const handlePrimaryAction = () => {
    if (shareUrl !== null) {
      void handleCopy();
      return;
    }
    void generateLink();
  };

  const primaryButtonLabel = (() => {
    if (loading) {
      return 'Generating link…';
    }
    if (copied) {
      return 'Copied';
    }
    if (shareUrl !== null) {
      return 'Copy link';
    }
    return 'Generate link';
  })();

  const linkInputValue = loading ? 'Generating link…' : (shareUrl ?? '');

  return (
    <ModalContent>
      <ModalHeader color="text.primary">
        Share Delivery Configuration
      </ModalHeader>
      <ModalCloseButton />
      <Divider orientation="horizontal" />
      <ModalBody>
        <Text style={{ marginBottom: '1rem' }}>
          Shared links allow Data Builder subscribers to start a new delivery
          from this configuration.
        </Text>

        {error !== null ? (
          <Text
            fontSize="sm"
            color="red.500"
            data-testid="data_builder_v2_share_error"
          >
            {error}
          </Text>
        ) : (
          <FormField label="Share Link">
            <Input
              value={linkInputValue}
              readOnly
              disabled={shareUrl === null}
              placeholder="Generate a link to share this configuration"
              data-testid="data_builder_v2_share_link_input"
              onFocus={(e: React.FocusEvent<HTMLInputElement>) =>
                e.target.select()
              }
            />
          </FormField>
        )}
      </ModalBody>
      <ModalFooter gap="3">
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
        <Button
          color="primary"
          onClick={handlePrimaryAction}
          disabled={loading}
          leftIcon={loading ? <Spinner size="xs" /> : undefined}
          data-testid="data_builder_v2_share_copy"
        >
          {primaryButtonLabel}
        </Button>
      </ModalFooter>
    </ModalContent>
  );
};

export const ShareLinkModal = ({
  isOpen,
  onClose,
  runId,
}: ShareLinkModalProps) => (
  <Modal isOpen={isOpen} onClose={onClose} isCentered>
    <ModalOverlay />
    {isOpen ? (
      <ShareLinkModalContent key={runId} runId={runId} onClose={onClose} />
    ) : null}
  </Modal>
);

type DataBuilderStatusRowMenuProps = {
  id: number;
  name: string;
  status: DataBuilderApiStatus;
  url: string | null;
  payload: DataBuilderPayload;
  onCanceled: () => void;
};

export const DataBuilderStatusRowMenu = ({
  id,
  name,
  status,
  url,
  payload,
  onCanceled,
}: DataBuilderStatusRowMenuProps) => {
  const navigate = useNavigate();
  const toast = useToast();
  const { kibanaLogger } = useKibanaLogger();
  const saveModal = useDisclosure();
  const shareModal = useDisclosure();
  const shareLinksEnabled = useUnleashFlag(FeatureFlag.DataBuilderShareLinks);
  const { canManageDatasets } = useDatasetStorage();
  const { mutateAsync: cancelRun } = useMutation(
    withDataBuilderMutationPolicy(
      {
        mutationKey: ['data-builder', 'cancel'],
        mutationFn: cancelDataBuilderRun,
      },
      kibanaLogger
    )
  );
  const canDownload = isDeliveryDownloadable(status, url);
  const canCancel = status === 'RUNNING';
  const downloadUnavailableLabel = (() => {
    if (!hasDownloadDeliveryMethod(payload)) {
      return 'Download is only available for deliveries using Download method';
    }
    if (!isFinishedDataBuilderStatus(status)) {
      return 'Download is only available when delivery is successful';
    }
    if (isDeliveryDownloadable(status, url)) {
      return '';
    }
    return 'Download is unavailable for this delivery';
  })();

  const handleDownload = () => {
    if (url !== null && url.trim().length > 0) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleDuplicateEdit = () => {
    const hydratedState = hydrateStoreFromPayload(payload);
    dataBuilderStore.getState().reset();
    dataBuilderStore.setState({
      ...hydratedState,
      loadedSavedDatasetId: null,
    });
    navigate(`/${PagePaths.DATA_BUILDER}/${DataBuilderPaths.DATASET}`);
  };

  const handleCancel = () => {
    showAlertDialog({
      alertType: 'confirm',
      isOpen: true,
      headerText: 'Cancel this run?',
      bodyContent: (
        <span>
          This will stop the run immediately. Any partial results will be
          discarded and the delivery will not complete.
        </span>
      ),
      footerAcceptIsDestructive: true,
      footerAcceptText: 'Yes, Cancel Run',
      onAccept: async () => {
        try {
          await cancelRun({ id });
          toast({
            title: 'Run canceled',
            status: 'success',
            duration: 4000,
            isClosable: true,
          });
          onCanceled();
        } catch (error: unknown) {
          const message =
            error instanceof Error ? error.message : 'Unknown error occurred';
          toast({
            title: 'Failed to cancel run',
            description: message,
            status: 'error',
            duration: 6000,
            isClosable: true,
          });
        }
      },
    });
  };

  return (
    <>
      <Menu placement="auto-end">
        <MenuButton
          as={IconButton}
          aria-label="Row actions"
          icon={<Icon as={IconDotGrid1x3Vertical} />}
          size="xs"
          colorScheme="gray"
          borderRadius="2px"
          className={styles.rowMenuButton}
          data-testid="data_builder_v2_status_row_menu_button"
          onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
            e.stopPropagation()
          }
        />
        <Portal>
          <MenuList
            zIndex={101}
            borderRadius="2px"
            className={styles.rowMenuList}
            onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => {
              e.stopPropagation();
            }}
          >
            {canManageDatasets && (
              <MenuItem
                icon={<Icon as={IconBookmark} boxSize={4} />}
                data-testid="data_builder_v2_status_save_config"
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  saveModal.onOpen();
                }}
              >
                Save Delivery Config
              </MenuItem>
            )}
            {shareLinksEnabled && (
              <MenuItem
                icon={<Icon as={IconChainLink2} boxSize={4} />}
                data-testid="data_builder_v2_status_share"
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  shareModal.onOpen();
                }}
              >
                Share Delivery Config
              </MenuItem>
            )}
            <MenuItem
              icon={<Icon as={IconPageEdit} boxSize={4} />}
              data-testid="data_builder_v2_status_duplicate_edit"
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                handleDuplicateEdit();
              }}
            >
              Duplicate & Edit
            </MenuItem>
            {canCancel && (
              <MenuItem
                icon={<Icon as={IconStop} boxSize={4} />}
                isDisabled={!canCancel}
                data-testid="data_builder_v2_status_cancel_run"
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  handleCancel();
                }}
              >
                Cancel Run
              </MenuItem>
            )}
            <Tooltip
              label={downloadUnavailableLabel}
              isDisabled={canDownload}
              hasArrow
              placement="left"
            >
              <MenuItem
                icon={<Icon as={IconFileDownload} boxSize={4} />}
                isDisabled={!canDownload}
                data-testid="data_builder_v2_status_download"
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  handleDownload();
                }}
              >
                Download
              </MenuItem>
            </Tooltip>
          </MenuList>
        </Portal>
      </Menu>
      <SaveConfigModal
        isOpen={saveModal.isOpen}
        onClose={saveModal.onClose}
        defaultName={name}
        payload={payload}
      />
      <ShareLinkModal
        isOpen={shareModal.isOpen}
        onClose={shareModal.onClose}
        runId={id}
      />
    </>
  );
};

type StatusGridRow = {
  id: number;
  name: string;
  client: string;
  created_at: string;
  updated_at: string;
  status: DataBuilderApiStatus;
  url: string | null;
  payload: DataBuilderPayload;
  snowflake_tables: DataBuilderStatusGetResponse[number]['snowflake_tables'];
  s3_paths: DataBuilderStatusGetResponse[number]['s3_paths'];
};

export const DataBuilderStatus = () => {
  const { loggedInUser } = useGetLoggedInUser();
  const showUserSelector = isRoleRevelioAdmin(loggedInUser?.role);
  const effectiveUserId = useEffectiveUserId();

  const [selectedUserOption, setSelectedUserOption] = useState<UserOption>();
  const [rows, setRows] = useState<DataBuilderStatusGetResponse>([]);
  const [fetching, setFetching] = useState(true);
  const [hasFetchedOnce, setHasFetchedOnce] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(() => new Date());
  const [timeDistance, setTimeDistance] = useState('');
  const [selectedRow, setSelectedRow] = useState<StatusGridRow | null>(null);
  const isThemedAppearance = useIsThemedAppearance();
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'created_at', desc: true },
  ]);

  /** Re-sync the picker when the effective user changes (impersonation applied or
   * lifted); a manual selection persists until then. */
  // biome-ignore lint/correctness/useExhaustiveDependencies: loggedInUser supplies display-only label fields; the picker must re-sync only when the effective user changes
  useEffect(() => {
    if (effectiveUserId) {
      setSelectedUserOption({
        value: effectiveUserId,
        label: loggedInUser.name || '',
        group: loggedInUser.client_name || '',
        secondaryLabel: loggedInUser.email || '',
      });
    }
  }, [effectiveUserId]);

  // While impersonating, the picker value differs from the real admin's
  // `loggedInUser.id`, so it's sent as `user_id` to fetch the impersonated
  // user's data; for the admin's own account none is sent (backend uses the session).
  const userIdParam =
    selectedUserOption && selectedUserOption.value !== loggedInUser?.id
      ? selectedUserOption.value
      : undefined;

  const loadRows = async (overrideUserId?: string) => {
    setFetching(true);
    try {
      const next = await fetchDataBuilderStatus(overrideUserId);
      setRows(next);
      setLastUpdate(new Date());
    } catch (error) {
      console.error(error);
    } finally {
      setFetching(false);
      setHasFetchedOnce(true);
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: poll uses initial loadRows; setState is stable
  useEffect(() => {
    void loadRows(userIdParam);
    const poll = window.setInterval(() => void loadRows(userIdParam), POLL_MS);
    return () => window.clearInterval(poll);
  }, [userIdParam]);

  useEffect(() => {
    setTimeDistance(formatDistanceToNow(lastUpdate, { addSuffix: true }));
    const id = window.setInterval(() => {
      setTimeDistance(formatDistanceToNow(lastUpdate, { addSuffix: true }));
    }, 60_000);
    return () => window.clearInterval(id);
  }, [lastUpdate]);

  const dataSource: StatusGridRow[] = rows.map((row) => ({
    id: row.id,
    name: row.name,
    client: row.client,
    created_at: row.date_created,
    updated_at: row.date_updated,
    status: row.status,
    url: row.url ?? null,
    payload: row.payload as DataBuilderPayload,
    snowflake_tables: row.snowflake_tables ?? null,
    s3_paths: row.s3_paths ?? null,
  }));

  const columns: ColumnDef<StatusGridRow, unknown>[] = [
    {
      accessorKey: 'id',
      header: 'ID',
      cell: (info) => {
        const value = info.getValue();
        return (
          <div data-testid={`${value}_data_builder_v2_status_row`}>
            {String(value ?? '')}
          </div>
        );
      },
    },
    { accessorKey: 'name', header: 'Name' },
    { accessorKey: 'client', header: 'Client' },
    {
      accessorKey: 'created_at',
      header: 'Creation',
      cell: (info) => (
        <div>
          {intlFormat(new Date(String(info.getValue())), {
            weekday: 'short',
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            timeZoneName: 'short',
          })}
        </div>
      ),
    },
    {
      accessorKey: 'updated_at',
      header: 'Updated',
      cell: (info) => {
        const date = new Date(String(info.getValue()));
        const fullDateTime = intlFormat(date, {
          weekday: 'short',
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          timeZoneName: 'short',
        });
        return (
          <Tooltip label={fullDateTime} hasArrow placement="top">
            <div>{formatDistanceToNow(date, { addSuffix: true })}</div>
          </Tooltip>
        );
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      enableSorting: false,
      cell: ({ getValue, row }) => {
        const value = getValue() as DataBuilderApiStatus;
        const data = row.original;
        const palette = dataBuilderTagStatusColorMap[value] ?? defaultTagColors;
        return (
          <Flex justifyContent="space-between" alignItems="center" gap={2}>
            <WithTooltip
              showCondition={isFinishedWithEmptyStatus(value)}
              label={FINISHED_WITH_EMPTY_TOOLTIP}
              placement="bottom"
            >
              {isThemedAppearance ? (
                <ThemedStatusTag
                  tone={dataBuilderTagStatusTone[value] ?? 'neutral'}
                >
                  {getDataBuilderStatusLabel(value)}
                </ThemedStatusTag>
              ) : (
                <Tag
                  bg={palette.bgColor}
                  color={palette.color}
                  borderRadius="4px"
                  fontWeight="semibold"
                  minWidth="94px"
                  textAlign="center"
                  py={1}
                  justifyContent="center"
                >
                  {getDataBuilderStatusLabel(value)}
                </Tag>
              )}
            </WithTooltip>
            <DataBuilderStatusRowMenu
              id={data.id}
              name={data.name}
              status={value}
              url={data.url}
              payload={data.payload}
              onCanceled={() => loadRows(userIdParam)}
            />
          </Flex>
        );
      },
    },
  ];

  const onRenderRow = useCallback(
    ({
      rowContent,
      rowData,
    }: {
      rowContent: ReactNode;
      rowData: StatusGridRow;
    }) => {
      if (!isValidElement(rowContent)) {
        return rowContent;
      }
      const originalClassName = rowContent.props?.className || '';
      const originalStyle = rowContent.props?.style || {};
      return (
        <tr
          className={originalClassName}
          style={{ ...originalStyle, cursor: 'pointer' }}
          onClick={() => setSelectedRow(rowData)}
        >
          {rowContent.props?.children}
        </tr>
      );
    },
    []
  );

  const hasRows = dataSource.length > 0;

  let tableContent: ReactNode;
  if (!hasRows && fetching) {
    tableContent = (
      <Flex h="100%" alignItems="center" justifyContent="center">
        <Spinner className={styles.loadingSpinner} />
      </Flex>
    );
  } else if (hasRows) {
    tableContent = (
      <TableChart
        data={dataSource}
        columns={columns}
        showZebraStripe={false}
        onRenderRow={onRenderRow}
        controlledSorting={{
          externalSortingState: sorting,
          onSortingChange: setSorting,
        }}
      />
    );
  } else {
    tableContent = (
      <Flex
        h="100%"
        alignItems="center"
        justifyContent="center"
        className={styles.emptyText}
      >
        <Text>No deliveries yet</Text>
      </Flex>
    );
  }

  return (
    <DashboardPage
      title={[PageTitles.DELIVERABLES, PageTitles.DELIVERABLES_STATUS]}
      loading={!hasFetchedOnce && fetching}
    >
      {showUserSelector && (
        <Box display="flex" justifyContent="space-between" alignItems="end">
          <UserSelector
            selectedUserOption={selectedUserOption}
            setSelectedUserOption={setSelectedUserOption}
            resetLabel="Reset to my runs"
            testId="data-builder-status-user-selector"
            selectableClientGroups={false}
          />
        </Box>
      )}
      <Box
        className={styles.gridContainer}
        rounded="lg"
        h={`calc(100vh - ${showUserSelector ? '169px' : '100px'})`}
        py={4}
        data-testid="data-builder-status-grid"
      >
        <Box className={styles.tableWrap} position="relative" h="100%" w="100%">
          <IconButton
            aria-label="Refresh status"
            icon={<IconArrowRotateLeftRight size={14} />}
            className={styles.refreshButton}
            position="absolute"
            top="8px"
            right="19px"
            zIndex={2}
            borderRadius="2px"
            colorScheme="gray"
            variant="outline"
            size="xs"
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              void loadRows(userIdParam);
            }}
          />
          {tableContent}
        </Box>
      </Box>
      <Flex alignItems="center" justifyContent="flex-end">
        <Text className={styles.lastUpdated} fontSize="xs" pt={2} opacity={0.5}>
          <Icon as={IconArrowRotateLeftRight} mr={1} top="1px" pos="relative" />{' '}
          <span>Last updated: {timeDistance}</span>
        </Text>
      </Flex>
      {selectedRow !== null && (
        <DataBuilderStatusDetailsModal
          isOpen
          onClose={() => setSelectedRow(null)}
          name={selectedRow.name}
          client={selectedRow.client}
          status={selectedRow.status}
          url={selectedRow.url}
          payload={selectedRow.payload}
          snowflake_tables={selectedRow.snowflake_tables}
          s3_paths={selectedRow.s3_paths}
        />
      )}
    </DashboardPage>
  );
};
